import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Field, FieldGroup, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { FormControl } from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  field: ControllerRenderProps<HttpActionForm, `parameters.${number}.default_value`>;
  index: number;
  isBoolean: boolean;
  isAutomatic: boolean;
};

const NO_DEFAULT = 'unset';
const EMPTY_DEFAULT = 'empty';

export const ParameterDefaultValue = ({ field, index, isBoolean, isAutomatic }: Props) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'sheet.form.data_to_collect.table' });
  const noDefault = field.value === null;

  if (isBoolean) {
    const selection =
      noDefault || (isAutomatic && field.value === '')
        ? NO_DEFAULT
        : field.value === ''
          ? EMPTY_DEFAULT
          : String(Number(field.value));

    return (
      <FormControl>
        <Select
          value={selection}
          onValueChange={(value) =>
            field.onChange(
              value === NO_DEFAULT
                ? isAutomatic
                  ? ''
                  : null
                : value === EMPTY_DEFAULT
                  ? ''
                  : value,
            )
          }
        >
          <SelectTrigger
            data-testid="boolean-default-value"
            aria-label={t('default_value')}
            className="w-full"
            size="sm"
            ref={field.ref}
            onBlur={field.onBlur}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={NO_DEFAULT} data-testid={`parameter-default-none-${index}`}>
                {t(isAutomatic ? 'default_value_unset' : 'default_value_none')}
              </SelectItem>
              {!isAutomatic && field.value === '' && (
                <SelectItem value={EMPTY_DEFAULT}>{t('default_value_empty')}</SelectItem>
              )}
              <SelectItem value="1">True</SelectItem>
              <SelectItem value="0">False</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
    );
  }

  return (
    <FieldGroup className="gap-2">
      <Field data-disabled={!isAutomatic && noDefault}>
        <FormControl>
          <Input
            {...field}
            value={field.value === null ? '' : String(field.value)}
            data-testid={`parameter-default-value-${index}`}
            aria-label={t('default_value')}
            size="sm"
            disabled={!isAutomatic && noDefault}
            placeholder={t(noDefault ? 'default_value_none' : 'default_value_placeholder')}
          />
        </FormControl>
      </Field>
      {!isAutomatic && (
        <Field orientation="horizontal" className="justify-start gap-2">
          <Checkbox
            id={`parameter-no-default-${index}`}
            data-testid={`parameter-no-default-${index}`}
            checked={noDefault}
            onCheckedChange={(checked) => field.onChange(checked ? null : '')}
          />
          <FieldLabel htmlFor={`parameter-no-default-${index}`}>
            {t('default_value_none')}
          </FieldLabel>
        </Field>
      )}
    </FieldGroup>
  );
};
